import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addGtmCandidate,
  approveGtmLead,
  completeGtmLeadReview,
  dismissGtmCandidate,
  draftGtmLeadSequenceMessages,
  enrichGtmCandidate,
  enrichGtmLead,
  findGtmLeads,
  getGtmApproveRiskRequired,
  getGtmLatestLeadSearch,
  getGtmLeadSequence,
  listGtmCandidates,
  listGtmLeads,
  patchGtmLeadSequenceMessages,
  stopGtmLeadSequence,
  type GtmLeadSequence,
  type GtmLeadStatusFilter,
} from "../api/GtmApi";
import { gtmQueryKeys } from "./gtmQueryKeys";

function invalidatePlayLeadQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  playId: string
) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: gtmQueryKeys.leadSearch(workspaceId, playId) }),
    queryClient.invalidateQueries({
      queryKey: [...gtmQueryKeys.all, "candidates", workspaceId, playId],
    }),
    queryClient.invalidateQueries({
      queryKey: [...gtmQueryKeys.all, "leads", workspaceId, playId],
    }),
  ]);
}

export function useGtmLeadSearchQuery(workspaceId: string, playId: string) {
  return useQuery({
    queryKey: gtmQueryKeys.leadSearch(workspaceId, playId),
    queryFn: () => getGtmLatestLeadSearch(workspaceId, playId),
    enabled: Boolean(workspaceId && playId),
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data?.status === "running" ? 2500 : false),
  });
}

export function useGtmCandidatesQuery(
  workspaceId: string,
  playId: string,
  status?: "found" | "dismissed"
) {
  return useQuery({
    queryKey: gtmQueryKeys.candidates(workspaceId, playId, status),
    queryFn: () => listGtmCandidates(workspaceId, playId, status),
    enabled: Boolean(workspaceId && playId),
    refetchOnWindowFocus: false,
  });
}

export function useGtmLeadsQuery(
  workspaceId: string,
  playId: string,
  status?: GtmLeadStatusFilter
) {
  return useQuery({
    queryKey: gtmQueryKeys.leads(workspaceId, playId, status),
    queryFn: () => listGtmLeads(workspaceId, playId, status),
    enabled: Boolean(workspaceId && playId),
    refetchOnWindowFocus: false,
  });
}

export function useGtmApproveRiskQuery(workspaceId: string) {
  return useQuery({
    queryKey: gtmQueryKeys.approveRisk(workspaceId),
    queryFn: () => getGtmApproveRiskRequired(workspaceId),
    enabled: Boolean(workspaceId),
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

export function useGtmFindLeadsMutation(workspaceId: string, playId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => findGtmLeads(workspaceId, playId),
    onSuccess: async (search) => {
      queryClient.setQueryData(gtmQueryKeys.leadSearch(workspaceId, playId), search);
      await invalidatePlayLeadQueries(queryClient, workspaceId, playId);
    },
  });
}

export function useGtmCandidateAddMutation(workspaceId: string, playId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (candidateId: string) => addGtmCandidate(workspaceId, candidateId),
    onSuccess: async () => {
      await invalidatePlayLeadQueries(queryClient, workspaceId, playId);
    },
  });
}

export function useGtmCandidateDismissMutation(workspaceId: string, playId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (candidateId: string) => dismissGtmCandidate(workspaceId, candidateId),
    onSuccess: async () => {
      await invalidatePlayLeadQueries(queryClient, workspaceId, playId);
    },
  });
}

export function useGtmCandidateEnrichMutation(workspaceId: string, playId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (candidateId: string) => enrichGtmCandidate(workspaceId, candidateId),
    onSuccess: async () => {
      await invalidatePlayLeadQueries(queryClient, workspaceId, playId);
    },
  });
}

export function useGtmLeadEnrichMutation(workspaceId: string, playId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) => enrichGtmLead(workspaceId, leadId),
    onSuccess: async () => {
      await invalidatePlayLeadQueries(queryClient, workspaceId, playId);
    },
  });
}

export function useGtmLeadApproveMutation(workspaceId: string, playId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { leadId: string; email: string; riskAcknowledged?: boolean }) =>
      approveGtmLead(workspaceId, input.leadId, {
        email: input.email,
        riskAcknowledged: input.riskAcknowledged,
      }),
    onSuccess: async () => {
      await Promise.all([
        invalidatePlayLeadQueries(queryClient, workspaceId, playId),
        queryClient.invalidateQueries({ queryKey: gtmQueryKeys.approveRisk(workspaceId) }),
      ]);
    },
  });
}

export function useGtmLeadCompleteReviewMutation(workspaceId: string, playId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) => completeGtmLeadReview(workspaceId, leadId),
    onSuccess: async () => {
      await invalidatePlayLeadQueries(queryClient, workspaceId, playId);
    },
  });
}

export function useGtmLeadStopMutation(workspaceId: string, playId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) => stopGtmLeadSequence(workspaceId, leadId),
    onSuccess: async () => {
      await invalidatePlayLeadQueries(queryClient, workspaceId, playId);
    },
  });
}

export function useGtmLeadSequenceQuery(workspaceId: string, leadId: string | null) {
  return useQuery({
    queryKey: gtmQueryKeys.leadSequence(workspaceId, leadId || ""),
    queryFn: () => getGtmLeadSequence(workspaceId, leadId!),
    enabled: Boolean(workspaceId && leadId),
    refetchOnWindowFocus: false,
  });
}

export function useGtmLeadSequenceDraftMutation(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) => draftGtmLeadSequenceMessages(workspaceId, leadId),
    onSuccess: (seq: GtmLeadSequence, leadId: string) => {
      queryClient.setQueryData(gtmQueryKeys.leadSequence(workspaceId, leadId), seq);
    },
  });
}

export function useGtmLeadSequenceMessagesPatchMutation(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { leadId: string; messages: { step: string; body: string }[] }) =>
      patchGtmLeadSequenceMessages(workspaceId, input.leadId, { messages: input.messages }),
    onSuccess: (seq: GtmLeadSequence, input) => {
      queryClient.setQueryData(gtmQueryKeys.leadSequence(workspaceId, input.leadId), seq);
    },
  });
}
